import { useMemo, useRef, useState } from "react";
import { fetchMarketsByCondition } from "@/api/polymarketApi";
import { useSignupMutation } from "@/hooks/mutations/useSignupMutation";
import { Empty, GridNote, Panel, SourceError } from "../components/Bits";
import { type Col, DataGrid } from "../components/DataGrid";
import { View } from "../components/Shell";
import { ViewHeader } from "../components/ViewHeader";
import { fileName, saveFile } from "../lib/csv";
import { useLive } from "../lib/data";
import { Updated } from "../lib/freshness";
import { cents, centsDelta, isoDay, toneClass, usd } from "../lib/format";
import { normalizeMarket } from "../lib/markets";
import { Link, openMarket } from "../lib/nav";
import { exportWatchlist, mergeWatchlist, moveSinceAdded, parseImport, useWatchlist, WATCH_MAX, type WatchItem } from "../lib/watchlist";

interface Row extends WatchItem {
	price: number | null;
	since: number | null;
	change: number | null;
	vol24: number | null;
	end: string | null;
	status: string;
}

const WATCH_REFRESH_MS = 60_000;
const IMPORT_MAX_BYTES = 1_000_000;
const HEADER_BUTTON = "h-7 border border-line bg-panel px-2 text-[11px] text-dim hover:text-fg disabled:opacity-40";

function EarlyAccessLine() {
	const [email, setEmail] = useState("");
	const { signup, isLoading, data, error } = useSignupMutation();
	if (data?.success) return <span className="text-up">you're on the list.</span>;
	return (
		<form
			className="flex min-w-0 flex-wrap items-center gap-1.5"
			onSubmit={(e: Event) => {
				e.preventDefault();
				if (email && !isLoading) void signup(email.trim());
			}}
		>
			<span>Alerts aren't built yet. Early access by email:</span>
			<input
				type="email"
				required
				aria-label="Email address"
				placeholder="mail@site.com"
				value={email}
				onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
				className="h-5 w-36 border border-line bg-panel px-1 text-[10px] text-bright placeholder:text-mute focus:border-up/60 focus:outline-none"
			/>
			<button type="submit" disabled={isLoading || !email} className="h-5 border border-line px-1.5 text-fg hover:border-up/50 hover:text-up disabled:opacity-40">
				{isLoading ? "…" : "join"}
			</button>
			{error || (data && !data.success) ? <span className="text-dn">{data?.error ?? "could not sign up right now"}</span> : null}
		</form>
	);
}

export function WatchlistView() {
	const { items, toggle, replace } = useWatchlist();
	const [notice, setNotice] = useState("");
	const picker = useRef<HTMLInputElement>(null);

	const onImport = async (file: File | undefined) => {
		if (!file) return;
		if (file.size > IMPORT_MAX_BYTES) {
			setNotice("That file is too large to be a watchlist.");
			return;
		}
		const parsed = parseImport(await file.text(), Date.now());
		if (!parsed) {
			setNotice("That file is not a watchlist.");
			return;
		}
		const merged = mergeWatchlist(items, parsed.items);
		replace(merged.list);
		const parts = [`added ${merged.added}`];
		if (merged.duplicate) parts.push(`${merged.duplicate} already pinned`);
		if (parsed.invalid) parts.push(`${parsed.invalid} not valid`);
		if (merged.overLimit) parts.push(`${merged.overLimit} over the ${WATCH_MAX} limit`);
		setNotice(parts.join(", "));
	};
	const ids = useMemo(() => items.map((i) => i.conditionId), [items]);
	const { data, error, isLoading, mutate, updatedAt } = useLive(ids.length ? ["pm:watch", ...ids] : null, (signal) => fetchMarketsByCondition(ids, signal), { refreshMs: WATCH_REFRESH_MS });

	const rows = useMemo<Row[]>(() => {
		const live = new Map((data ?? []).map((m) => [m.conditionId, normalizeMarket(m)]));
		return items.map((i) => {
			const m = live.get(i.conditionId);
			return {
				...i,
				question: m?.question ?? i.question,
				price: m?.outcomes[0]?.price ?? null,
				since: moveSinceAdded(m?.outcomes[0]?.price, i.addedPrice),
				// Gamma keeps the last 24h change on a closed market for good. It is not a live figure.
				change: m?.closed ? null : (m?.change24 ?? null),
				vol24: m?.vol24 ?? null,
				end: m?.endDate ?? null,
				status: !m ? "–" : m.closed ? "closed" : "open",
			};
		});
	}, [items, data]);

	const cols = useMemo<Col<Row>[]>(
		() => [
			{ key: "q", label: "Market", w: 150, grow: 4, sort: (r) => r.question, cell: (r, narrow) => <span className={narrow ? "clamp-2 text-bright" : "text-bright"} title={r.question}>{r.question}</span> },
			{ key: "p", label: "Now", w: 48, right: true, sort: (r) => r.price, cell: (r) => <span className="text-bright">{cents(r.price)}</span> },
			{
				key: "a",
				label: "Since added",
				title: "Price of the first outcome now, minus its price when you pinned it. Blank if pinned before prices were kept.",
				w: 72,
				right: true,
				sort: (r) => r.since,
				cell: (r) => <span className={toneClass(r.since)}>{centsDelta(r.since)}</span>,
			},
			{ key: "d", label: "24h", w: 56, right: true, prio: 1, sort: (r) => r.change, cell: (r) => <span className={toneClass(r.change)}>{centsDelta(r.change)}</span> },
			{ key: "v", label: "Vol 24h", w: 62, right: true, prio: 2, sort: (r) => r.vol24, cell: (r) => usd(r.vol24) },
			{ key: "e", label: "Resolves", w: 76, right: true, prio: 3, sort: (r) => r.end, cell: (r) => <span className="text-dim">{isoDay(r.end)}</span> },
			{ key: "s", label: "Status", w: 48, prio: 4, sort: (r) => r.status, cell: (r) => <span className="text-dim">{r.status}</span> },
			{
				key: "x",
				label: "",
				w: 44,
				right: true,
				cell: (r) => (
					<button
						type="button"
						className="border border-line px-1 text-[10px] text-dn hover:border-dn/50"
						onClick={(e: MouseEvent) => {
							e.stopPropagation();
							toggle(r);
						}}
					>
						remove
					</button>
				),
			},
		],
		[toggle],
	);

	const body = items.length === 0 ? (
		<Empty>
			Nothing pinned yet. Open any market from <Link href="/" className="text-up hover:underline">unusual markets</Link> and press
			{" "}<span className="text-bright">+ watch</span>.
		</Empty>
	) : error && !data ? (
		<SourceError source="Polymarket (gamma-api)" onRetry={() => void mutate()} />
	) : undefined;

	return (
		<View>
			<ViewHeader
				title="watchlist"
				meta={items.length ? `${items.length} pinned` : undefined}
				desc="Markets you pinned. Stored in this browser only, so it does not follow you to another device."
				tabs={false}
			>
				{notice ? <span role="status" className="text-[11px] text-dim">{notice}</span> : null}
				<button type="button" disabled={items.length === 0} onClick={() => saveFile(fileName("watchlist", "json", new Date()), exportWatchlist(items, Date.now()), "application/json")} className={HEADER_BUTTON}>
					export
				</button>
				<button type="button" onClick={() => picker.current?.click()} className={HEADER_BUTTON}>
					import
				</button>
				<input
					ref={picker}
					type="file"
					accept="application/json,.json"
					aria-label="Import a watchlist file"
					className="hidden"
					onChange={(e) => {
						const input = e.currentTarget as HTMLInputElement;
						void onImport(input.files?.[0]);
						input.value = "";
					}}
				/>
			</ViewHeader>
			<Panel>
				<DataGrid
					label="Watchlist"
					cols={cols}
					rows={rows}
					rowKey={(r) => r.conditionId}
					onRow={(r) => openMarket(r.conditionId)}
					csvName="watchlist"
					loading={isLoading && !data && items.length > 0}
					body={body}
				/>
				<GridNote><Updated at={updatedAt} refreshMs={WATCH_REFRESH_MS} failing={Boolean(error)} alone /><EarlyAccessLine /></GridNote>
			</Panel>
		</View>
	);
}
